<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useFileBrowserActions } from "@/ui/components/FileBrowser/useFileBrowserActions";
import {
	fileActionAvailability,
	folderActionAvailability,
} from "@/ui/components/FileBrowser/actionAvailability";
import FileDTO = App.Data.FileDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

const props = defineProps<{
	file: FileDTO | null;
	folder?: ResourceTreeNodeDO | null;
	target?: HTMLElement | [number, number];
	/** How many rows the right-click happened inside, files and folders alike. */
	selectionCount?: number;
	canRemoveSelection?: boolean;
}>();

const isOpen = defineModel<boolean>();

const emit = defineEmits<{
	"edit-selected-file": [];
	"share-resource": [];
	"toggle-favorite-resource": [];
	"remove-file": [];
	"edit-folder": [folder: ResourceTreeNodeDO];
	"remove-folder": [folder: ResourceTreeNodeDO];
}>();

const { t } = useI18n();
const { hasAction } = useFileBrowserActions();

const availability = computed(() =>
	fileActionAvailability(props.file, hasAction)
);

const folderAvailability = computed(() =>
	folderActionAvailability(props.folder ?? null, hasAction)
);

type Action = {
	key: string;
	icon: string;
	label: string;
	isAvailable: boolean;
	isDestructive?: boolean;
	run: () => void;
};

const folderActions = computed<Action[]>(() =>
	[
		{
			key: "edit-folder",
			icon: "fa: fa-solid fa-pen",
			label: t("fileBrowser.actions.edit"),
			isAvailable: folderAvailability.value.edit,
			run: () => props.folder && emit("edit-folder", props.folder),
		},
		{
			key: "remove-folder",
			icon: "fa: fa-solid fa-trash",
			label: t("fileBrowser.actions.delete"),
			isAvailable: folderAvailability.value.remove,
			isDestructive: true,
			run: () => props.folder && emit("remove-folder", props.folder),
		},
	].filter((action) => action.isAvailable)
);

const fileActions = computed<Action[]>(() =>
	[
		{
			key: "edit",
			icon: "fa: fa-solid fa-pen",
			label: t("fileBrowser.actions.edit"),
			isAvailable: availability.value.edit,
			run: () => emit("edit-selected-file"),
		},
		{
			key: "share",
			icon: "fa: fa-duotone fa-rectangle-history-circle-user",
			label: t("fileBrowser.actions.share"),
			isAvailable: availability.value.share,
			run: () => emit("share-resource"),
		},
		{
			key: "favorite",
			icon: "fa: fa-solid fa-house",
			label: props.file?.is_favorite
				? t("fileBrowser.actions.removeAsHome")
				: t("fileBrowser.actions.setAsHome"),
			isAvailable: availability.value.toggleFavorite,
			run: () => emit("toggle-favorite-resource"),
		},
		{
			key: "remove",
			icon: "fa: fa-solid fa-trash",
			label: t("fileBrowser.actions.delete"),
			isAvailable: availability.value.remove,
			isDestructive: true,
			run: () => emit("remove-file"),
		},
	].filter((action) => action.isAvailable)
);

const isBatch = computed(() => (props.selectionCount ?? 0) > 1);

const batchActions = computed<Action[]>(() =>
	props.canRemoveSelection
		? [
				{
					key: "remove-selection",
					icon: "fa: fa-solid fa-trash",
					label: t("fileBrowser.actions.deleteSelected", props.selectionCount),
					isAvailable: true,
					isDestructive: true,
					run: () => emit("remove-file"),
				},
		  ]
		: []
);

const actions = computed<Action[]>(() => {
	if (isBatch.value) {
		return batchActions.value;
	}

	return props.folder ? folderActions.value : fileActions.value;
});

const runAction = (action: Action): void => {
	isOpen.value = false;
	action.run();
};
</script>

<template>
	<v-menu
		v-model="isOpen"
		v-if="actions.length > 0"
		:target="target"
		location="bottom end"
	>
		<v-list density="compact" min-width="200" class="py-1">
			<v-list-item
				v-for="action in actions"
				:key="action.key"
				:base-color="action.isDestructive ? 'error' : undefined"
				:data-testid="action.isDestructive ? 'delete-resource' : undefined"
				@click="runAction(action)"
			>
				<template #prepend>
					<v-icon
						size="12"
						:color="
							action.key === 'favorite' && file?.is_favorite
								? 'warning'
								: action.key === 'remove'
								? 'error'
								: 'primary'
						"
					>
						{{ action.icon }}
					</v-icon>
				</template>

				<v-list-item-title>{{ action.label }}</v-list-item-title>
			</v-list-item>
		</v-list>
	</v-menu>
</template>

<style lang="scss" scoped>
.v-list {
	--v-list-prepend-gap: 8px;
}

.v-list-item-title {
	font-size: 13px;
	line-height: 1.4;
	letter-spacing: normal;
}
</style>
